import { Modal, Radio } from "antd";
import Button from "Components/Button";
import { useState } from "react";
import { SearchBox } from "../SearchBox";
import { useDebounce } from "hooks/useDebounce";
import Loader from "Components/Loader";
import { cn } from "utils";
import {
  OrderDrivers,
  useOrder,
  useOrderDrivers,
} from "hooks/OrderManagement/useOrders";

type ManualOrderCustomerSelectionProps = {
  orderId: string;
};

export const OrderDriverSelection = ({
  orderId,
}: ManualOrderCustomerSelectionProps) => {
  const [selectedDriver, setSelectedDriver] = useState<OrderDrivers | null>();
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  const debouncedSearch = useDebounce(customerSearch);

  const { data, isPending } = useOrderDrivers({
    search: debouncedSearch,
  });

  const {
    data: orderData,
    assignDriver,
    assignDriverLoading,
  } = useOrder(orderId);

  const order = orderData?.data;

  const drivers = data?.data.docs || [];

  const getCustomerById = (id: string) => {
    return drivers.find((customer) => customer.id === id) || null;
  };

  const getCustomerFullName = () => {
    return `${selectedDriver?.firstName ?? ""} ${
      selectedDriver?.lastName ?? ""
    }`;
  };

  const onModalClose = (clearCustomer = false) => {
    setShowCustomerModal(false);
    if (clearCustomer) {
      setSelectedDriver(null);
    }
  };

  const onSubmit = () => {
    if (!selectedDriver?.id) return;

    assignDriver(
      {
        driverId: selectedDriver?.id,
      },
      {
        onSuccess: () => {
          onModalClose();
        },
      }
    );
  };

  return (
    <div>
      <Button
        label="Assign driver for shipping"
        onClick={() => setShowCustomerModal(true)}
      />
      <Modal
        title="Drivers"
        open={showCustomerModal}
        className="max-w-[40rem] !w-full"
        onCancel={() => onModalClose()}
        footer={
          <Button
            className={`text-white p-[12px] w-full font-bold rounded-md ease-in-out duration-300`}
            label={"Continue"}
            disabled={!selectedDriver}
            isLoading={assignDriverLoading}
            onClick={onSubmit}
          />
        }>
        <div className="w-full">
          <div className="w-full grid justify-between grid-cols-[1fr_auto] gap-x-2">
            <SearchBox
              value={customerSearch}
              onChange={setCustomerSearch}
              placeholder="Search Name, ID, Email Address"
              className="rounded-xl bg-[#F3F3F3]"
            />
          </div>

          {isPending && <Loader />}

          {/* list */}
          <Radio.Group
            value={selectedDriver?.id}
            onChange={(e) => setSelectedDriver(getCustomerById(e.target.value))}
            className="w-full"
            name="customer">
            <div className="max-h-[70vh] overflow-y-auto py-4 w-full">
              {drivers.map((driver) => (
                <Radio
                  key={driver.id}
                  value={driver.id}
                  className={cn(
                    "flex items-center space-x-2 border-b border-border px-4 py-3",
                    driver.id === selectedDriver?.id ? "bg-primary-light" : ""
                  )}>
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <div className="size-11 rounded-full bg-gray font-bold justify-center items-center flex bg-primary/20">
                      {driver.firstName[0].toUpperCase()}
                      {driver.lastName[0].toUpperCase()}
                    </div>
                    {/* User Details */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium">
                        {driver.firstName} {driver.lastName}
                      </h3>
                      <p className="text-sm text-gray-500 font-normal">
                        {driver.email}
                      </p>
                    </div>
                  </div>
                </Radio>
              ))}
            </div>
          </Radio.Group>
        </div>
      </Modal>
    </div>
  );
};
